import * as THREE from 'three';
import type { Weather } from '../../shared/types.js';
import { graphicsPrefs, type Quality } from '../graphicsPrefs.js';
import { ATMOSPHERE, ATMOSPHERE_GLSL, installAtmosphere, setAtmosphere } from './atmosphere.js';
import { createPost, type Grade, type Post } from './post.js';
import { createPrecipitation, lookFor, type Precipitation, type WeatherLook } from './weather.js';

// Before any material in the game compiles, or the first ones would keep three's own fog.
installAtmosphere();

/**
 * The renderer, the lights, the sky and the sun's shadow. Kept apart from anything
 * game-specific so the visual setup can be tuned without touching the race logic.
 *
 * One directional light, one hemisphere light, and the sky itself as an environment map —
 * baked from the same model the dome and the haze are drawn with (`atmosphere.ts`), so what
 * glass and paint reflect is the sky you can see. Light is kept in a real range rather than
 * clipped at 1 — a sunlit wall is brighter than white paper in shade — and a tone curve
 * brings it back to the screen (DECISIONS.md §80).
 *
 * The one thing here that is not free by day is the shadow map, and it earns its place:
 * buildings casting onto the road is the difference between a city and a diagram. It is kept
 * affordable by never covering more than a few hundred metres around the player — see
 * `fitShadowCamera`.
 *
 * **Two settings.** On `high` every frame goes through `post.ts` — a half-float target, the
 * grade, a bloom that only catches what is brighter than white, FXAA. On `low` the frame is
 * drawn straight to the canvas with the tone curve applied in each material, by night as well
 * as by day, at one pixel per CSS pixel, and nothing reflects the sky: the hemisphere light
 * stands in for the light the sky map gave (DECISIONS.md §102). It is the default on a touch
 * screen, where the GPU is a phone's.
 *
 * Everything visual is driven by `applyWeather`, which is handed the real conditions at the
 * real place.
 */

export type { Quality };

export interface SceneKit {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  resize(width: number, height: number): void;
  /** Call once per frame before rendering — keeps the sky, the rain and the shadow with the camera. */
  update(dt?: number): void;
  /** Draws the frame, through the post chain or straight to the canvas — see `Quality`. */
  render(): void;
  /** Off drops the shadow pass entirely; `?shadows=off` does the same from the URL. */
  setShadowsEnabled(enabled: boolean): void;
  readonly shadowsEnabled: boolean;
  setQuality(quality: Quality): void;
  readonly quality: Quality;
  /**
   * The miniature look for the cameras that look down on the city, 0–1 — see `post.ts`.
   * It needs the post chain, so asking for it on `low` by day runs the chain while it is on.
   */
  setTiltShift(amount: number): void;
  /** The conditions at the region. Null goes back to the default clear midday look. */
  applyWeather(weather: Weather | null): void;
  /**
   * Pulls the fog in closer than the weather has it, for a shot that needs the distance
   * hidden; null hands it back. Never pushes it further out — a foggy day stays foggy.
   */
  setFogRange(range: { near: number; far: number } | null): void;
  /**
   * How far the camera draws, in metres, or null for everything there is. The haze closes in
   * ahead of it so the city fades out rather than ending at an edge — see DECISIONS.md §92.
   */
  setViewDistance(metres: number | null): void;
  /** What the last `applyWeather` resolved to. Read by the tests. */
  readonly look: WeatherLook;
  dispose(): void;
}

/**
 * The sun's direction, and how far up it the shadow camera sits.
 *
 * A directional light has no position in any physical sense; this one is placed relative to
 * whatever the shadow camera is currently framing, so the light travels with the player and
 * the shadow map is never spent on a part of the city nobody can see. The *direction* is
 * mutable because it is now the real sun's, which moves — see `weather.ts`.
 */
const SUN_DIRECTION = new THREE.Vector3(-260, 420, 180).normalize();
const SUN_DISTANCE = 700;
/**
 * 4096 on `high`: at street level the box is about 260 m across, so 2048 texels put six
 * centimetres of kerb under each, and a kerb's shadow, a lamp post's and a wheel's are all
 * about that size. The normal bias goes down with it — it is measured in texels — which is
 * what puts things back on the ground instead of floating a hand's width above their shadows.
 */
const SHADOW_MAP_SIZES = { high: 4096, low: 2048 } as const;
const SHADOW_NORMAL_BIAS = { high: 0.35, low: 0.8 } as const;

/**
 * On `low` the sky is not an environment map, and the hemisphere light is raised by this much
 * of what the map's intensity was, to give back the light it took with it. Matched by the mean
 * brightness of the same frame with and without the map: 2.0 by day, about 2.6 at golden hour
 * and in rain, and anything at night, where the map is dim. Sampling the map was 13–18% of a
 * `low` frame at a phone's resolution, the most of any one thing but the pixel count.
 */
const ENVIRONMENT_STAND_IN = 2.4;

/**
 * Lightning, in seconds between flashes and how fast one decays.
 *
 * Irregular on purpose: a strike every six seconds on the dot is a strobe light. The decay
 * is fast — a flash you can read the duration of is a flash that looks like a light being
 * switched on — and roughly a third of them fire twice, which is what makes the rest read
 * as lightning rather than as a bug.
 */
const FLASH_GAP = [3.5, 15] as const;
const FLASH_DECAY = 7;
/** The flash's colour, in linear light, and how much of it each part of the scene takes. */
const FLASH_WHITE = new THREE.Color(0xdfe9f7);

/**
 * The sun's disc, in linear light. Far above white, so the bloom finds it and the tone curve
 * rolls it off into a glare rather than a flat yellow coin.
 */
const SUN_DISC = 9;

/**
 * Which tone curve. ACES is New York's, and it is the default: with the light balanced for
 * it (§80) the toe that §14 blamed for "mud" is what gives a shaded street its depth.
 * `?tonemap=agx` and `?tonemap=neutral` are kept for comparing — AgX is gentler with
 * saturated colour, and Neutral is close to no curve at all below about 0.8.
 */
const TONE_MAPPINGS: Record<string, THREE.ToneMapping> = {
  aces: THREE.ACESFilmicToneMapping,
  agx: THREE.AgXToneMapping,
  neutral: THREE.NeutralToneMapping,
};

export interface SceneOptions {
  /**
   * A lightning strike, for whoever wants to put a sound on it — fired once per strike,
   * not once per flash, since the quick second flash of a double is the same strike and
   * has no clap of its own. The renderer does not know or care what happens next; the delay
   * between this and the thunder is `ambience.ts`'s business, because it is the distance.
   */
  onLightning?(): void;
}

export function createSceneKit(canvas: HTMLCanvasElement, options: SceneOptions = {}): SceneKit {
  const params = typeof location === 'undefined' ? new URLSearchParams() : new URLSearchParams(location.search);
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: 'high-performance',
    // Needed so Playwright can screenshot the canvas without a second render pass.
    preserveDrawingBuffer: true,
  });
  let look = lookFor(null);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = TONE_MAPPINGS[params.get('tonemap') ?? ''] ?? THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;

  let quality = graphicsPrefs.quality;
  const applyPixelRatio = () => {
    // Above 2x the fill cost stops buying anything visible on a laptop panel; on `low` the
    // pixel count is the single biggest saving there is. A phone is 3x, so one in nine.
    const cap = quality === 'high' ? 2 : 1;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio ?? 1, cap));
  };
  applyPixelRatio();

  const scene = new THREE.Scene();
  // Near and far only. The colour is the sky's, per fragment — see `atmosphere.ts` — and
  // `fog.color` is left black for the lightning to add to.
  const fog = new THREE.Fog(0x000000, look.fogNear, look.fogFar);
  scene.fog = fog;

  const sky = makeSkyDome();
  scene.add(sky.mesh);

  const camera = new THREE.PerspectiveCamera(62, 1, 0.5, FULL_VIEW);
  camera.position.set(0, 12, 22);

  const hemi = new THREE.HemisphereLight(look.hemiSky, look.hemiGround, look.hemiIntensity);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(look.keyColor, look.keyIntensity);
  sun.position.copy(SUN_DIRECTION).multiplyScalar(SUN_DISTANCE);
  sun.castShadow = true;
  // A city is nearly all large flat surfaces meeting at right angles, which is the worst
  // case for shadow acne. `normalBias` pushes the lookup along the surface normal, which
  // fixes it without the peter-panning a large depth bias would cause.
  sun.shadow.bias = -0.0004;
  const applyShadowQuality = () => {
    const size = SHADOW_MAP_SIZES[quality];
    if (sun.shadow.mapSize.x !== size) {
      sun.shadow.mapSize.set(size, size);
      // The map is reallocated at the new size on the next shadow pass.
      sun.shadow.map?.dispose();
      sun.shadow.map = null;
    }
    sun.shadow.normalBias = SHADOW_NORMAL_BIAS[quality];
  };
  applyShadowQuality();
  sun.shadow.camera.near = 10;
  sun.shadow.camera.far = SUN_DISTANCE + 800;
  scene.add(sun);
  scene.add(sun.target);

  // --- the sky as a light ----------------------------------------------------
  //
  // Rebaked when the weather changes and at no other time: a few milliseconds once, against
  // a sky that is otherwise the same for the whole race. The lightning does not rebake it —
  // a flash lasts a tenth of a second, and the lights carry it.
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envSky = makeSkyDome({ environment: true });
  const envScene = new THREE.Scene();
  envScene.add(envSky.mesh);
  let envTarget: THREE.WebGLRenderTarget | null = null;
  const bakeEnvironment = () => {
    envSky.setGround(look.hemiGround);
    const next = pmrem.fromScene(envScene, 0, 0.1, 100);
    envTarget?.dispose();
    envTarget = next;
    scene.environmentIntensity = look.envIntensity;
    applyEnvironment();
  };
  /** The sky map on `high`, none on `low`. Switching recompiles every material once. */
  const applyEnvironment = () => {
    const next = quality === 'high' ? (envTarget?.texture ?? null) : null;
    if (scene.environment === next) return;
    scene.environment = next;
    scene.traverse((object) => {
      const material = (object as THREE.Mesh).material;
      if (!material) return;
      for (const m of Array.isArray(material) ? material : [material]) m.needsUpdate = true;
    });
  };
  /** The hemisphere light, with the stand-in for the sky map on `low`. */
  const hemiIntensity = () =>
    look.hemiIntensity + (quality === 'low' ? look.envIntensity * ENVIRONMENT_STAND_IN : 0);

  // Two independent switches, and both have to be on. The player's is a preference; the
  // weather's is a fact — there is nothing to cast at midnight or under a storm — and
  // conflating them meant a night race silently turned the player's setting off for good.
  let userWantsShadows = true;
  let shadowsEnabled = true;
  const syncShadows = () => {
    const enabled = userWantsShadows && look.shadows;
    if (enabled === shadowsEnabled) return;
    shadowsEnabled = enabled;
    renderer.shadowMap.enabled = enabled;
    sun.castShadow = enabled;
    // Materials compiled against a shadow-mapped light have to be rebuilt without one.
    scene.traverse((object) => {
      const material = (object as THREE.Mesh).material;
      if (!material) return;
      for (const m of Array.isArray(material) ? material : [material]) m.needsUpdate = true;
    });
  };
  const setShadowsEnabled = (enabled: boolean) => {
    userWantsShadows = enabled;
    syncShadows();
  };
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  if (params.get('shadows') === 'off') setShadowsEnabled(false);

  // --- post ----------------------------------------------------------------

  const bloomAllowed = params.get('bloom') !== 'off';
  let post: Post | null = null;
  /** Built the first time it is needed: a `low` daytime race never pays for it. */
  const ensurePost = () => {
    if (post) return post;
    post = createPost(renderer, scene, camera);
    post.resize();
    return post;
  };
  const grade: Grade = { ...look.grade };
  let tiltShift = 0;

  const resize = (width: number, height: number) => {
    renderer.setSize(width, height, false);
    camera.aspect = width / Math.max(1, height);
    camera.updateProjectionMatrix();
    post?.resize();
  };

  const render = () => {
    // Not after dark on `low` either, though it costs the city's lights their glow: at night
    // the chain was a quarter of a `low` frame at a phone's resolution. The cameras over the
    // city still get their miniature look, for the few seconds they are up.
    const usePost = quality === 'high' || tiltShift > 0.01;
    if (!usePost) {
      renderer.render(scene, camera);
      return;
    }
    Object.assign(grade, look.grade);
    // On `low` the glow is the night's alone, and comes up with the lights, as it always did.
    const bloomScale = !bloomAllowed ? 0 : quality === 'high' ? 1 : look.night;
    grade.bloomStrength = look.grade.bloomStrength * bloomScale;
    grade.tiltShift = tiltShift;
    ensurePost().render(grade);
  };

  const setQuality = (next: Quality) => {
    if (next === quality) return;
    quality = next;
    applyPixelRatio();
    applyShadowQuality();
    applyEnvironment();
    applyFlash(flash);
    const size = renderer.getSize(new THREE.Vector2());
    resize(size.x, size.y);
  };

  // --- weather -------------------------------------------------------------

  let precipitation: Precipitation | null = null;
  let flash = 0;
  let nextFlashIn = flashGap();
  /** Whether the flash that is due next was scheduled as the second half of a double. */
  let nextIsRepeat = false;

  /**
   * Everything the lightning touches, including the case where there is none.
   *
   * The flash is added on top of the palette rather than replacing it, so this is also how
   * the palette gets applied in the first place — one path, and no way for a scene to be
   * left holding the brightened version of itself.
   */
  const applyFlash = (amount: number): void => {
    sun.intensity = look.keyIntensity + amount * 8;
    hemi.intensity = hemiIntensity() + amount * 2.6;
    fog.color.copy(FLASH_WHITE).multiplyScalar(amount * 0.8);
    sky.setFlash(amount * 0.7);
  };

  let fogRange: { near: number; far: number } | null = null;
  let viewDistance: number | null = null;
  const syncFog = (): void => {
    let near = fogRange ? Math.min(look.fogNear, fogRange.near) : look.fogNear;
    let far = fogRange ? Math.min(look.fogFar, fogRange.far) : look.fogFar;
    // The haze is 97% at its far end at street level and thinner higher up, so it has to
    // be complete well before the far plane, or the tops of the last towers are cut off in
    // clear air.
    if (viewDistance !== null) {
      far = Math.min(far, viewDistance * VIEW_FOG_SHARE);
      near = Math.min(near, far * 0.35);
    }
    fog.near = near;
    fog.far = far;
  };
  const setViewDistance = (metres: number | null): void => {
    const next = metres === null ? null : Math.min(FULL_VIEW, Math.max(200, metres));
    if (next === viewDistance) return;
    viewDistance = next;
    camera.far = next ?? FULL_VIEW;
    camera.updateProjectionMatrix();
    // The dome rides with the camera and must stay inside the far plane, or the sky is
    // clipped away to the clear colour.
    sky.mesh.scale.setScalar(Math.min(1, (camera.far * 0.9) / SKY_RADIUS));
    syncFog();
  };
  const setFogRange = (range: { near: number; far: number } | null): void => {
    fogRange = range;
    syncFog();
  };

  const applyWeather = (weather: Weather | null): void => {
    look = lookFor(weather);
    SUN_DIRECTION.copy(look.keyDirection);
    sun.color.setHex(look.keyColor);
    hemi.color.setHex(look.hemiSky);
    hemi.groundColor.setHex(look.hemiGround);
    setAtmosphere(
      {
        zenith: look.skyTop,
        horizon: look.skyHorizon,
        haze: look.fog,
        glow: look.glow,
        glowStrength: look.glowStrength,
        skyIntensity: 1,
      },
      look.keyDirection,
    );
    // No disc for the stand-in moon: it is a light, not a body anyone placed in the sky.
    sky.setSun(look.night < 1 && look.keyDirection.y > 0 ? look.keyColor : null, 1 - look.night);
    renderer.setClearColor(look.fog);
    syncFog();
    syncShadows();
    bakeEnvironment();

    if (precipitation) {
      scene.remove(precipitation.object);
      precipitation.dispose();
      precipitation = null;
    }
    if (look.precipitation) {
      precipitation = createPrecipitation(look.precipitation.kind, look.precipitation.intensity);
      scene.add(precipitation.object);
    }

    flash = 0;
    nextFlashIn = flashGap();
    nextIsRepeat = false;
    applyFlash(0);
  };
  applyWeather(null);

  return {
    renderer,
    scene,
    camera,
    resize,
    render,
    setShadowsEnabled,
    setQuality,
    setTiltShift(amount) {
      tiltShift = Math.max(0, Math.min(1, amount));
    },
    applyWeather,
    setFogRange,
    setViewDistance,
    get shadowsEnabled() {
      return shadowsEnabled;
    },
    get quality() {
      return quality;
    },
    get look() {
      return look;
    },
    update(dt = 0) {
      // The dome is smaller than the world, so it has to travel with the camera —
      // otherwise the far side falls outside the far plane and the sky turns black.
      sky.mesh.position.copy(camera.position);
      precipitation?.update(camera, dt);

      if (look.lightning) {
        nextFlashIn -= dt;
        if (nextFlashIn <= 0) {
          flash = 1;
          const isRepeat = nextIsRepeat;
          // A third of strikes flash twice. Scheduling the second one as a very short gap
          // reuses the same machinery rather than adding a second timer for it.
          nextIsRepeat = Math.random() < 0.34;
          nextFlashIn = nextIsRepeat ? 0.11 + Math.random() * 0.1 : flashGap();
          // One clap per strike: the second flash of a double is the same bolt.
          if (!isRepeat) options.onLightning?.();
        }
        if (flash > 0) {
          flash = Math.max(0, flash - dt * FLASH_DECAY);
          applyFlash(flash);
        }
      }

      if (shadowsEnabled) fitShadowCamera(sun, camera, sun.shadow.mapSize.x);
    },
    dispose() {
      if (precipitation) {
        scene.remove(precipitation.object);
        precipitation.dispose();
        precipitation = null;
      }
      post?.dispose();
      post = null;
      envTarget?.dispose();
      pmrem.dispose();
      envSky.dispose();
      sky.dispose();
      hemi.dispose();
      sun.dispose();
      renderer.dispose();
    },
  };
}

function flashGap(): number {
  return FLASH_GAP[0] + Math.random() * (FLASH_GAP[1] - FLASH_GAP[0]);
}

// ---------------------------------------------------------------------------
// shadow camera
// ---------------------------------------------------------------------------

const _focus = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);

/** The far plane when nothing asks for less: the whole of the largest region and its horizon. */
const FULL_VIEW = 2600;
const SKY_RADIUS = 1800;
/** Where the haze has to be complete, as a share of the view distance. */
const VIEW_FOG_SHARE = 0.8;

/**
 * Aims the sun's shadow camera at a box around the player rather than around the city.
 *
 * A 2 km region at a usable shadow resolution would need a 16k map. What the player can
 * actually see is a few hundred metres, so the box is sized from the camera's height —
 * about 260 m at street level, up to 620 m from the aerial view where the shadows only have
 * to read as shapes — and centred a little way ahead of where they are looking.
 *
 * The centre is then snapped to whole shadow texels in the *light's* own basis. Without
 * that the shadow map resamples every frame and every shadow edge in the city crawls as you
 * drive, which is far more distracting than no shadows at all.
 */
function fitShadowCamera(sun: THREE.DirectionalLight, camera: THREE.PerspectiveCamera, mapSize: number): void {
  camera.getWorldDirection(_forward);
  _forward.y = 0;
  if (_forward.lengthSq() < 1e-8) _forward.set(0, 0, -1);
  else _forward.normalize();

  const height = Math.max(0, camera.position.y);
  const radius = THREE.MathUtils.clamp(height * 1.6 + 200, 260, 620);
  const lead = THREE.MathUtils.clamp(height * 0.7, 25, 150);
  _focus.set(
    camera.position.x + _forward.x * lead,
    0,
    camera.position.z + _forward.z * lead,
  );

  // The shadow camera's own axes: it looks down SUN_DIRECTION with world up as its up.
  _right.crossVectors(_worldUp, SUN_DIRECTION).normalize();
  _up.crossVectors(SUN_DIRECTION, _right).normalize();
  const texel = (radius * 2) / mapSize;
  const alongRight = Math.round(_focus.dot(_right) / texel) * texel;
  const alongUp = Math.round(_focus.dot(_up) / texel) * texel;
  const alongSun = _focus.dot(SUN_DIRECTION);
  _focus
    .copy(_right)
    .multiplyScalar(alongRight)
    .addScaledVector(_up, alongUp)
    .addScaledVector(SUN_DIRECTION, alongSun);

  sun.target.position.copy(_focus);
  sun.target.updateMatrixWorld();
  sun.position.copy(_focus).addScaledVector(SUN_DIRECTION, SUN_DISTANCE);

  const shadowCamera = sun.shadow.camera;
  if (shadowCamera.right !== radius) {
    shadowCamera.left = -radius;
    shadowCamera.right = radius;
    shadowCamera.top = radius;
    shadowCamera.bottom = -radius;
    shadowCamera.updateProjectionMatrix();
  }
}

/**
 * An inside-out sphere drawn with `atmoSky` — the same function the haze on every material
 * uses, so a building fading into the distance fades into exactly the sky behind it.
 *
 * A dome rather than `scene.background`: it is unaffected by aspect ratio and it renders
 * behind everything with depth writes off for free.
 *
 * The environment variant is the same sky with the sun's disc left out — a point that bright
 * in an environment map turns every glossy surface into a mirror of it — and the ground put
 * in below the horizon, warm and dark like the street, so nothing is lit from underneath by
 * blue sky.
 */
function makeSkyDome(opts: { environment?: boolean } = {}): {
  mesh: THREE.Mesh;
  setSun(color: number | null, strength: number): void;
  setFlash(amount: number): void;
  setGround(color: number): void;
  dispose(): void;
} {
  // Comfortably inside the camera's far plane, since the dome rides with the camera.
  const geometry = new THREE.SphereGeometry(opts.environment ? 50 : SKY_RADIUS, 32, 16);
  const uniforms = {
    ...ATMOSPHERE,
    sunColor: { value: new THREE.Color(0, 0, 0) },
    flash: { value: new THREE.Color(0, 0, 0) },
    groundColor: { value: new THREE.Color(0, 0, 0) },
  };
  const material = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms,
    defines: opts.environment ? { ENVIRONMENT: '' } : {},
    vertexShader: /* glsl */ `
      varying vec3 vDir;
      void main() {
        vDir = position;
        gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4( position, 1.0 );
      }
    `,
    fragmentShader: /* glsl */ `
      ${ATMOSPHERE_GLSL}
      uniform vec3 sunColor;
      uniform vec3 flash;
      uniform vec3 groundColor;
      varying vec3 vDir;
      void main() {
        vec3 dir = normalize( vDir );
        vec3 col = atmoSky( dir );
        #ifdef ENVIRONMENT
          // Half the saturation of the sky you see. What lights a surface is the whole dome
          // at once, and most of it is the pale band near the horizon; lit by the zenith's
          // colour, every shadow in the city came out navy.
          col = mix( vec3( dot( col, vec3( 0.2126, 0.7152, 0.0722 ) ) ), col, 0.5 );
          col = mix( col, groundColor, smoothstep( 0.0, -0.12, dir.y ) );
        #else
          // About half a degree across, with a soft limb so it is round at any resolution.
          col += sunColor * smoothstep( 0.99989, 0.99996, dot( dir, atmoSunDir ) );
        #endif
        gl_FragColor = vec4( col + flash, 1.0 );
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'sky';
  mesh.renderOrder = -1000;
  mesh.frustumCulled = false;
  return {
    mesh,
    setSun(color, strength) {
      if (color === null) uniforms.sunColor.value.setRGB(0, 0, 0);
      else uniforms.sunColor.value.setHex(color).multiplyScalar(SUN_DISC * strength);
    },
    setFlash(amount) {
      uniforms.flash.value.copy(FLASH_WHITE).multiplyScalar(amount);
    },
    setGround(color) {
      // Dark: what is below the horizon from a street is mostly other things' shadowed sides.
      uniforms.groundColor.value.setHex(color).multiplyScalar(0.35);
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}
